import clsx from 'clsx';
import RTECompose from 'contentful/RTE_compose';
import Accordion from 'contentful/accordion';
import ImageGrid from 'contentful/image_grid';
import QuoteCarousel from 'contentful/quote_carousel';
import FullScreenVideo from 'contentful/full_screen_video';
import ImageCarousel from 'contentful/image_carousel';
import BlogCarousel from 'contentful/blog_carousel';
import useContentfulPreview from 'hooks/useContentfulPreview';
import EventSidebar from './event_sidebar';
import EventHero from './event_hero';
import ScheduleGroup from './schedule_group';
import EventIntro from './event_intro';
import SponsorsGrid from './event_sponsors';
import EventLocation from './event_location';
import MetaData from '../../contentful/meta_data';

const EventShow = ({ content, relatedPosts }) => {
  const { fields } = useContentfulPreview(content);

  const {
    heroImage,
    title,
    subTitle,
    registerButton,
    dateTime,
    location,
    standNumber,
    freeToAttendDescription,
    freeToAttendTitle,
    facebookUrl,
    xUrl,
    instagramUrl,
    tiktokUrl,
    linkedinUrl,
    hashtag,
    eventOverview,
    dailyAgendaTitle,
    dailyAgendaDescription,
    dailyAgenda,
    eventScheduleTitle,
    eventScheduleDescription,
    eventSchedule,
    reasonsToAttend,
    eventHosts,
    imageGallery,
    video,
    sponsorsExhibitorsTitle,
    sponsorsExhibitors,
    sponsorsExhibitorsButton,
    attendeeFeedback,
    faqs,
    seoTitle,
    seoDescription,
    canonicalUrl,
  } = fields;

  const sidebarProps = {
    registerButton,
    dateTime,
    location,
    standNumber,
    freeToAttendDescription,
    freeToAttendTitle,
  };

  const hasSidebarContent = Object.values(sidebarProps).some(
    prop => prop !== undefined,
  );

  return (
    <div className="contentful-page mt-65">
      <MetaData
        title={seoTitle}
        description={seoDescription}
        canonicalUrl={canonicalUrl}
      />
      <div className="v2-container">
        <EventHero heroImage={heroImage || {}} />
        <div className={clsx(hasSidebarContent && 'contentful__grid')}>
          {hasSidebarContent && (
            <EventSidebar {...sidebarProps} className="d-none l-d-block" />
          )}

          <div className="event__main-content">
            <EventIntro
              title={title}
              subTitle={subTitle}
              sharingLinks={{
                ...(facebookUrl ? { facebookUrl } : {}),
                ...(xUrl ? { xUrl } : {}),
                ...(instagramUrl ? { instagramUrl } : {}),
                ...(tiktokUrl ? { tiktokUrl } : {}),
                ...(linkedinUrl ? { linkedinUrl } : {}),
              }}
              hashtag={hashtag}
            />

            {hasSidebarContent && (
              <EventSidebar {...sidebarProps} className="l-d-none" />
            )}

            {eventOverview && (
              <section className="event__overview event__section">
                <RTECompose content={eventOverview} fieldType="content" />
              </section>
            )}

            {reasonsToAttend?.fields.gridItems.length > 0 && (
              <section className="event__reasons-to-attend event__section">
                <ImageGrid data={reasonsToAttend.fields} />
              </section>
            )}

            {eventHosts?.fields.gridItems.length > 0 && (
              <section className="event__hosts event__section">
                <ImageGrid data={eventHosts.fields} perRow={3} />
              </section>
            )}

            {eventSchedule && (
              <div className="event__event-schedule event__section">
                {eventScheduleTitle && <h2>{eventScheduleTitle}</h2>}
                {eventScheduleDescription && <p>{eventScheduleDescription}</p>}
                <ScheduleGroup
                  title={eventSchedule.fields.title}
                  agenda={eventSchedule.fields.scheduleItems}
                  key={eventSchedule.sys.id}
                />
              </div>
            )}

            {dailyAgenda?.length > 0 && (
              <div className="event__daily-agenda event__section">
                {dailyAgendaTitle && <h2>{dailyAgendaTitle}</h2>}
                {dailyAgendaDescription && <p>{dailyAgendaDescription}</p>}
                {dailyAgenda.map(
                  ({
                    sys: { id },
                    fields: { title: groupTitle, scheduleItems },
                  }) => (
                    <ScheduleGroup
                      title={groupTitle}
                      agenda={scheduleItems}
                      key={id}
                    />
                  ),
                )}
              </div>
            )}

            {video && (
              <section className="event__section">
                <FullScreenVideo
                  key={video.sys.id}
                  id={video.sys.id}
                  item={{
                    link: video.fields.videoUrl,
                    title: video.fields.title,
                  }}
                />
              </section>
            )}

            {location && <EventLocation location={location} />}

            {imageGallery && (
              <section className="event__image-gallery event__section">
                <h2>Image Gallery</h2>
                <ImageCarousel
                  id={imageGallery.sys.id}
                  content={imageGallery.fields}
                />
              </section>
            )}

            {attendeeFeedback?.length > 0 && (
              <section className="event__attendee-feedback event__section">
                <h2>Past Attendee Feedback</h2>
                <QuoteCarousel quotes={attendeeFeedback} />
              </section>
            )}

            {faqs && (
              <section className="event__faq event__section">
                <Accordion id={faqs.sys.id} fields={faqs.fields} />
              </section>
            )}
          </div>
        </div>
        {sponsorsExhibitors?.length > 0 && (
          <section className="event__section--padding">
            <SponsorsGrid
              title={sponsorsExhibitorsTitle}
              sponsors={sponsorsExhibitors}
              btnText={sponsorsExhibitorsButton}
            />
          </section>
        )}
      </div>

      {relatedPosts?.length > 0 && (
        <section className="event__section--padding bg-grey-3">
          <div className="v2-container">
            <h2>Blog Posts</h2>
            <BlogCarousel posts={relatedPosts} />
          </div>
        </section>
      )}
    </div>
  );
};

export default EventShow;
